import React, {
  Fragment,
  useCallback, useEffect, useMemo, useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import { useRouter } from 'next/router';
import { updateDealStep, userSelectionsSelector } from '@/builders/deals/slice/userSelections.slice';
import { dealSelectors } from '@/builders/deals/slice/deal.slice';
import useSodiumAlertForStore from '@/clientCore/components/SodiumWarningDisclaimer/hooks/useSodiumAlertForStore';
import {
  SodiumWarningDisclaimer
} from '@/clientCore/components/SodiumWarningDisclaimer/components/SodiumWarningDisclaimer';
import { isCyo, isMeltProduct } from '@/clientCore/menu/utils/pizzaCategories';
import { CRUST_SUBTYPE, SIZE_SUBTYPE } from '@/clientCore/menu/utils/pizzaNutrition';
import { useCCGetDealBuilderQuery } from '@/clientCore/temporaryTransformationalHooks/useCCGetDealBuilderQuery';
import CenteredContainer from '@/common/CenteredContainer';
import CategoryTitle from '@/common/components/CategoryTitle';
import formattedPrice from '@/common/formattedPrice';
import ProductId from '@/common/ProductId';
import { getSizeCrust } from '@/common/ProductDetails/hooks/useGetSizeCrustDetails';
import CaloriesDisclaimer from '@/common/CaloriesDisclaimer/CaloriesDisclaimer';

import { getNutritionInfo } from '../../utils/pizzaNutrition';
import { getRecipeUpchargeAmount } from '../../utils/pizzaPrices';
import css from './MultiRecipeSelection.module.css';
import MultiRecipeSelectionSkeleton from './MultiRecipeSelection.skeleton';
import MultiRecipeSelectionTile, { RecipeTileData } from './MultiRecipeSelectionTile';
import { CurrentStep } from '@/builders/deals/slice/dealTypes';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import { onPizzaMenuTileClickInsideDeal } from '@/builders/deals/analytics';
import { DealPizzaBuilder } from '@/builders/deals/DealStepToRender/DealStep';
import { BuiltPizza, PizzaIngredient, PizzaIngredientOption } from '@/builders/pizza/dataTransformers/builderTypes';
import useAddToDeal from '@/builders/deals/hooks/useAddToDeal';
import { MenuRecipe } from '@/menu/pizza/pizzaMenuTypes';
import { getMenuItems, onMenuInitialLoadAnalytics } from '@/dataAnalytics/dataAnalyticsHelper';
import { useApplicationInitialized } from '@/hooks/useApplicationInitialized';
import { MELTS_RECIPE_PAGE_VIEW, MULTI_CLASS_PAGE_VIEW, PIZZA_RECIPE_PAGE_VIEW } from '@/menu/pizza/constants';
import { getOrInitializeOptimizely } from '../../../../../optimizely/optimizely';
import { closeModal, openModal } from '@/localization/actions';
import { oneClickDealProductOOSBody } from '@/builders/pizza/hooks/useOutOfStockModal/constants';
import DealType from '@/builders/deals/DealTypes';
import { parseRecipeToBuiltPizza } from '@/cart/helpers/getUserRecipes';
import { useMenuVariantContent } from '@/api/tanStackQueries/menuVariantContent';
import MultiRecipeSelectionTileWithSizeOption from './MultiRecipeSelectionTileWithSizeOption';
import CountdownTimer from '@/common/countdownTimer/CountdownTimer';
import {
  checkIsPastEndTime,
  checkIsPastIntervalStartTime,
  getTimerDetails
} from '@/common/countdownTimer/getTimerDetails';
import { localizationSelectors } from '@/localization/localizationSelectors';
import { showDealNotinTimeWindowModal } from '@/common/Modal/modalDetails';
import Routes from '@/router/routes';

type PizzaMap = {
  cyoRecipes: RecipeTileData[];
  upchargeRecipes: RecipeTileData[];
};

interface Props {
  isEditFlow?: boolean;
  isAddToCartLoading?: boolean;
  isQuickAdd?: boolean;
}

function MultiRecipeSelection(props: Props): JSX.Element {
  const { isQuickAdd, isAddToCartLoading } = props;
  const { id: dealId, name: dealName } = useSelector(dealSelectors.selectDealSummary);
  const occasion = useSelector(localizationSelectors.occasion);
  const storeDetails = useSelector(localizationSelectors.storeDetails);
  const { rawData, loading: isLoading } = useCCGetDealBuilderQuery({ itemId: dealId });
  const appInitialized = useApplicationInitialized();
  const optimizelyInstance = getOrInitializeOptimizely();
  const dispatch = useDispatch();
  const analytics = useAnalytics();
  const addToDeal = useAddToDeal();
  const dealType = useSelector(dealSelectors.selectDealType);
  const router = useRouter();
  const hasPushedPageLoadAnalytics = useRef(false);
  const [{ enabled: isDealSingleStepMultiRecipePriceEnabled }] = useDecision('fr-dtg-1621-deals-single-step-multi-recipe-price');
  const [{enabled: isMultiClassBundleStepSubHeaderTextHideEnabled}] = useDecision('fr-dtg-1599-remove-upgrade-text-multi-class-bundle-step');
  const [{enabled: isMhbEntreePageStreamlineEnhancementsEnabled}] = useDecision('dtg-1584-mhb-entree-page-streamline-enhancements');
  const [{ enabled: showMeltsSizes }] = useDecision('dtg-1596-melts-sizes-buttons');
  const [{ enabled: isShowDealPppContentEnabled }] = useDecision('fr-dtg-1770-show-deal-ppp-content');
  const [{ enabled: isTimerBannerOnRecipePageEnabled }] = useDecision('fr-dtg-1710-timer-banner-on-recipe-page');
  const [{ enabled: isNewDealTimeExpiresErrorEnabled }] = useDecision('fr-ims-599-fix-dtg-1778');
  const [{ enabled: isStoreTimezoneAvailabilityEnabled }] = useDecision('fr-ims-614-crafted-flatzz-timezone-bug');

  const deal = useSelector(dealSelectors.selectDeal);
  const [selectedRecipeId, setSelectedRecipeId] = useState<string | null>(null);
  const currentStep = useSelector(userSelectionsSelector.selectCurrentStep);
  const recipes = useSelector(
    userSelectionsSelector.selectRecipesForCurrentStep
  );
  const {data: variants} = useMenuVariantContent();
  const builtPizzaForSelectedRecipeId = parseRecipeToBuiltPizza(selectedRecipeId ?? '', recipes ?? []);
  const stepData = rawData?.steps?.find((step) => step.id === currentStep?.id);
  const isSingleStepDeal = rawData?.steps?.length === 1;
  const { isShowSodiumAlertForStore } = useSodiumAlertForStore();
  const isMelt = isMeltProduct(dealName);
  const recipeTypes = isMultiClassBundleStepSubHeaderTextHideEnabled ? recipes?.reduce<string []>((acc, recipe) => {
    if(!acc.includes(recipe?.type)){
      acc.push(recipe?.type);
    }
    return acc;
  }, []) || [] : [];
  const isMultiClassBundleStep = recipeTypes.length > 1 && isMultiClassBundleStepSubHeaderTextHideEnabled;
  const {duration, intervalStartTime} = getTimerDetails(deal?.data?.availability, occasion, storeDetails?.storeTimezone);
  const showTimerBanner = isTimerBannerOnRecipePageEnabled && isSingleStepDeal && duration && intervalStartTime;

  useEffect(() => () => {
      setSelectedRecipeId(null);
  }, []);

  useEffect(() => {
    if (appInitialized && !hasPushedPageLoadAnalytics.current && stepData?.recipes?.length) {
      const label = 'Deals';
      const items = getMenuItems(stepData?.recipes as unknown as MenuRecipe[], label);
      const pageViewMap = {
        melt: MELTS_RECIPE_PAGE_VIEW,
        pizza: PIZZA_RECIPE_PAGE_VIEW,
        multiClass: MULTI_CLASS_PAGE_VIEW
      }
      let pageView = pageViewMap.pizza;
      if(isMelt && !isMultiClassBundleStep) {
        pageView = pageViewMap.melt;
      } else if(isMultiClassBundleStep) {
        pageView = pageViewMap.multiClass;
      }
      analytics.push(() => onMenuInitialLoadAnalytics(
        label,
        items,
        dealId,
        dealName
      ));

      optimizelyInstance?.track(pageView);
      hasPushedPageLoadAnalytics.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, dealName, hasPushedPageLoadAnalytics.current, appInitialized, isMelt, stepData?.recipes?.length]);

  const checkDealTimeExpired = useCallback((): boolean => {
    const isPastEndTime = Boolean(duration && intervalStartTime && checkIsPastEndTime(storeDetails?.storeTimezone, duration, intervalStartTime));
    const now = isStoreTimezoneAvailabilityEnabled ? new Date(new Date().toLocaleString('en-US', { timeZone: storeDetails?.storeTimezone })) : new Date();
    const isPastIntervalStartTime = checkIsPastIntervalStartTime(now, intervalStartTime );
    if (isNewDealTimeExpiresErrorEnabled && (isPastEndTime || !isPastIntervalStartTime)) {
      const onClose = () => router.push(Routes.DEALS);
      dispatch(openModal(showDealNotinTimeWindowModal(onClose)));
      return true;
    }

    return false;
  }, [duration, intervalStartTime, storeDetails?.storeTimezone, isStoreTimezoneAvailabilityEnabled, isNewDealTimeExpiresErrorEnabled, dispatch, router]);

  const handleCustomizeClick = useCallback((recipeId: string, customPageTitle?: string) => {
    if(checkDealTimeExpired()) return;
    const recipe = recipes?.find((r) => r.id === recipeId);
    const recipeType = recipe?.type ?? '';
    
    const newCurrentStep: Partial<CurrentStep> = {
      ...currentStep,
      recipeId,
      recipeType,
      ...(isShowDealPppContentEnabled && customPageTitle && { customPageTitle })
    };
    setSelectedRecipeId(recipeId)
    dispatch(updateDealStep(newCurrentStep));

    if (dealId && dealName && recipe) {
      const selectedPizzaIndex = recipes?.indexOf(recipe);
      analytics.push(() => onPizzaMenuTileClickInsideDeal(
        dealName,
        dealId,
        recipe,
        selectedPizzaIndex ?? 0
      ));
    }
  }, [dispatch, currentStep, recipes, dealId, dealName, analytics, isShowDealPppContentEnabled, checkDealTimeExpired]);

  // Out of stock modal
  const getOOSModalDetails = useCallback((oosSelectedOptions:PizzaIngredientOption[], pizza: BuiltPizza, upcharge: number): ModalContent => ({
    title: 'Topping so good, it\'s gone',
    body: oneClickDealProductOOSBody.replaceAll('[ingredients]', oosSelectedOptions.map((option) => option.name).join(', ')),
    altCta: {
      text: 'Choose another Recipe',
      reverseButtonsOrder: true,
      callback: () => dispatch(closeModal())
    },
    cta: {
      text: 'Add to Deal',
      reverseButtonsOrder: true,
      callback: () => {
        // Remove out of stock toppings from the pizza and add it to the deal
        addToDeal(
          {
            ...pizza,
            meatToppings: (pizza.meatToppings as unknown as [PizzaIngredientOption]).filter(
              (topping) => !topping.outOfStock
            ) as unknown as PizzaIngredient[],
            veggieToppings: (pizza.veggieToppings as unknown as [PizzaIngredientOption]).filter(
              (topping) => !topping.outOfStock
            ) as unknown as PizzaIngredient[]
          },
          false,
          upcharge,
          isQuickAdd
        );
        dispatch(closeModal());
      }
    },
    hideCloseIcon: false
  }), [dispatch, addToDeal, isQuickAdd]);

  const handleAddToDeal = useCallback(((recipeId: string, upcharge: number, quantity = 1, selectedSize?: PizzaIngredientOption) => {
    if (!recipes) return;
    const pizza = parseRecipeToBuiltPizza(recipeId, recipes, selectedSize);
    if (!pizza) return;

    // Check if any of the selected toppings are out of stock
    const outOfStockToppings = ([...pizza.meatToppings ?? [], ...pizza.veggieToppings ?? []] as unknown as [PizzaIngredientOption]).filter(
      (topping) => topping.outOfStock
    );

    // If any of the selected toppings are out of stock, show a modal
    if (outOfStockToppings?.length > 0) {
      const modalDetails = getOOSModalDetails(outOfStockToppings, pizza, upcharge);
      dispatch(openModal(modalDetails));
      return;
    }

    const pizzaWithUpdatedQuantity = {
      ...pizza,
      quantity
    }

    addToDeal(pizzaWithUpdatedQuantity, false, upcharge, isQuickAdd);
  }), [recipes, addToDeal, isQuickAdd, getOOSModalDetails, dispatch]);

  const pizzaMap = useMemo(() => stepData?.recipes?.reduce((map: PizzaMap, currrentRecipe) => {
    const {
      id, description, selectedOptions, options, outOfStock, price
    } = currrentRecipe;
    
    let { name, imageURL } = currrentRecipe;

    let variantApplied = false;
    if (currrentRecipe.type === "PIZZA" && isMhbEntreePageStreamlineEnhancementsEnabled) {

      const pizzaSizeOption = options?.find(option => option.id === "option_pizza_size");
      
      if (pizzaSizeOption?.modifiers?.length === 1) {
        const sizeModifier = pizzaSizeOption.modifiers[0];
        
        if (sizeModifier?.modifiers?.length === 1) {
          const innerModifier = sizeModifier.modifiers[0];

          const variantCode = innerModifier?.variantCode;
          const variant = variantCode ? variants?.variants?.[variantCode] : undefined;

          if (variant) {
            name = variant.variantName;
            imageURL = variant.variantImage?.url;
            variantApplied = true;
          }
        }
      }
    }

    const { globalId: productId } = new ProductId(id ?? '');
    const upchargeAmount = getRecipeUpchargeAmount(options);
    const isCyoRecipe = !!isCyo(currrentRecipe);
    const sizes = options?.find((option) => option?.id.includes('size'))?.modifiers || []
    const showGetStartedExperience = isCyoRecipe || (sizes.length > 1 && !isSingleStepDeal);
    let updatedMap: PizzaMap;
    const showSizeAndCrustSelection = !showGetStartedExperience && ((!isMelt && !isMultiClassBundleStep) || (isMeltProduct(currrentRecipe.name) && sizes.length > 1 && showMeltsSizes));
    const shouldShowPrice = dealType === DealType.SINGLE_STEP && (isDealSingleStepMultiRecipePriceEnabled ? !upchargeAmount : isMelt);

    const recipeData: RecipeTileData = {
      id,
      isCyo: isCyoRecipe,
      name,
      description,
      outOfStock,
      imageURL,
      sizeCrustSelection: isCyoRecipe ? '' : getSizeCrust(selectedOptions, [SIZE_SUBTYPE, CRUST_SUBTYPE]),
      upchargeAmount: upchargeAmount === 0 || outOfStock ? '' : formattedPrice(upchargeAmount, upchargeAmount % 100 === 0),
      formattedPrice: formattedPrice((price || 0) + upchargeAmount),
      onCustomize: () => handleCustomizeClick(productId, variantApplied ? name : ''),
      onAddToDeal: (quantity = 1, sizeID) => {
        if(checkDealTimeExpired()) return;
        const selectedSize = sizes.find(option => option.id === sizeID)
        const sizeOverride: PizzaIngredientOption | undefined = selectedSize ? {
          description: selectedSize.description,
          glutenFree: selectedSize.glutenFree,
          id: selectedSize.id,
          image: undefined,
          name: selectedSize.name,
          nutrition: selectedSize.nutrition ?? null,
          outOfStock: false,
          price: selectedSize.price,
          priority: undefined,
          selected: true,
          splittable: selectedSize.splittable,
          type: selectedSize.type
        } : undefined;
        const finalUpcharge = sizeID && selectedSize ? 
          (selectedSize.modifiers?.find(mod => mod.upcharge != null)?.upcharge ?? upchargeAmount) : 
          upchargeAmount;
        handleAddToDeal(productId, finalUpcharge, quantity, sizeOverride)
      },
      nutritionInfo: isCyoRecipe ? '' : getNutritionInfo(selectedOptions),
      showGetStartedExperience,
      showSizeAndCrustSelection,
      primaryCtaButtonText: isQuickAdd ? 'Add to Cart' : 'Add to deal',
      shouldShowPrice,
      shouldShowUpchargeBadge: !shouldShowPrice && !outOfStock && !!upchargeAmount,
      shouldShowActionRow: !outOfStock,
      useVariantName: variantApplied,
      sizeOptions: sizes?.map(size => ({ 
        name: size.name, 
        value: size.id,
        upcharge: size.modifiers?.find(mod => mod.upcharge != null)?.upcharge
      })) || []
    };

    if (recipeData.isCyo) {
      updatedMap = { ...map, cyoRecipes: [...map.cyoRecipes, recipeData] };
    } else {
      updatedMap = { ...map, upchargeRecipes: [...map.upchargeRecipes, recipeData] };
    }

    return updatedMap;
  }, { cyoRecipes: [], upchargeRecipes: [] }),
  [
    stepData?.recipes, 
    isSingleStepDeal, 
    isMelt, 
    isMultiClassBundleStep, 
    dealType, 
    isDealSingleStepMultiRecipePriceEnabled, 
    isQuickAdd, 
    handleCustomizeClick, 
    handleAddToDeal, 
    variants,
    isMhbEntreePageStreamlineEnhancementsEnabled,
    showMeltsSizes,
    checkDealTimeExpired
  ]);

  const sortedRecipes = pizzaMap?.upchargeRecipes.sort((a, b) => {
    if (a.outOfStock && !b.outOfStock) return 1;
    if (!a.outOfStock && b.outOfStock) return -1;
    return 0;
  });

  if (isLoading) {
    return (
      <MultiRecipeSelectionSkeleton />
    );
  }

  if (currentStep?.recipeId) return <DealPizzaBuilder builtPizzaForSelectedRecipeId={builtPizzaForSelectedRecipeId} {...props} />;

  if (!stepData || !pizzaMap) {
    return <div>Step not found</div>; // TODO: Error handling
  }

  const recipeHeadingText = pizzaMap.upchargeRecipes.some((recipe) => recipe.upchargeAmount === '' && !recipe.outOfStock) ? 'Choose a recipe' : 'Or upgrade to a recipe';

  return (
    <CenteredContainer>
      <div className={css.container} data-testid="multi-recipe-selection">
        {isMelt ? <CategoryTitle title="Melts" />
          : <CategoryTitle title={stepData.name ?? ''} />}

        { showTimerBanner && <div className={css['product-tile__timer-container']}>
          <CountdownTimer intervalStartTime={intervalStartTime} duration={duration} $variant="recipe" dealName={dealName}/>
        </div>}

        {pizzaMap.cyoRecipes?.map(
          (product) => (
            <MultiRecipeSelectionTile key={product.id} product={product} stepName={stepData.name} isSingleStepDeal={isSingleStepDeal} />)
        )}

        {!isMelt
          && <h2 data-testid="recipe-upgrade-header" className={css['upcharge-recipes__heading']}>{!isMultiClassBundleStep ? recipeHeadingText : ''}</h2>}

        <div className={css['upcharge-recipes__container']}>
            {sortedRecipes?.map((product) => (
              <Fragment key={product.id}>
                {isMeltProduct(product.name) && showMeltsSizes && (product.sizeOptions?.length ?? 0) > 1 ? (
                  <MultiRecipeSelectionTileWithSizeOption
                    isLoading={isAddToCartLoading && product.id === deal?.userSelections.present.recipes[0].item.id}
                    product={product}
                    stepName={stepData.name}
                    />
                ) : (
                  <MultiRecipeSelectionTile
                    product={product}
                    stepName={stepData.name}
                    isLoading={isAddToCartLoading && product.id === deal?.userSelections.present.recipes[0].item.id}
                    isSingleStepDeal={isSingleStepDeal}
                    />
                )}
              </Fragment>
            ))}
        </div>

        <CaloriesDisclaimer />
        {isShowSodiumAlertForStore && <SodiumWarningDisclaimer />}
      </div>
    </CenteredContainer>
  );
}

export { MultiRecipeSelection };
